import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Switch,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  Paper,
  MenuItem,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import {
  doc,
  updateDoc,
  collection,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db, updateUserEmail, deleteUser } from "../../config/firebase";
import { splitName, joinName } from "../../utils/name";
import { formatDateTime } from "../../utils/formatDate";
import ModalDialogTitle from "../common/ModalDialogTitle";
import ModalActions from "../common/ModalActions";

const EditTpInstructor = ({
  open,
  onClose,
  instructor,
  onSuccess,
  onUserDeleted,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  // Tracks the email the modal opened with, so submit only calls the
  // Auth-touching updateUserEmail callable when it actually changed.
  const [originalEmail, setOriginalEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [ext, setExt] = useState("");
  const [notes, setNotes] = useState("");
  const [sccId, setSccId] = useState("");
  const [sinclairEmail, setSinclairEmail] = useState("");
  const [eLearnInstructor, setELearnInstructor] = useState(false);
  const [dentalInstructor, setDentalInstructor] = useState(false);
  const [pltwInstructor, setPltwInstructor] = useState(false);
  const [academicInstructor, setAcademicInstructor] = useState(false);
  const [userTypes, setUserTypes] = useState({
    staff: false,
    coordinator: false,
    instructor: false,
    admin: false,
    pathwayManager: false,
  });
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  // Partner and Pathways for dropdowns
  const [partner, setPartner] = useState("");
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [pathways, setPathways] = useState([]);
  const [pathwaysLoading, setPathwaysLoading] = useState(true);
  const [pathway, setPathway] = useState("");
  const [siteId, setSiteId] = useState("");
  const [active, setActive] = useState("-1"); // -1 = active, 0 = inactive
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    if (!value) return "";
    const phoneNumber = value.replace(/\D/g, "");
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
        3,
        6,
      )}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (setter) => (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setter(formatted);
  };

  useEffect(() => {
    if (instructor) {
      // Basic fields
      if (instructor.firstName || instructor.lastName) {
        setFirstName(instructor.firstName || "");
        setLastName(instructor.lastName || "");
      } else {
        const split = splitName(instructor.name);
        setFirstName(split.firstName);
        setLastName(split.lastName);
      }
      const instructorEmail = instructor.email || instructor.InstWorkEmail || "";
      setEmail(instructorEmail);
      setOriginalEmail(instructorEmail);
      setPhone(instructor.phone ? formatPhoneNumber(instructor.phone) : "");
      // Initialize active flag (-1 active, 0 inactive)
      setActive(
        instructor.Active !== undefined
          ? String(instructor.Active)
          : instructor.active !== undefined
            ? String(instructor.active)
            : "-1",
      );
      // Initialize site id from several possible fields
      setSiteId(
        instructor.SOD_Site_ID ||
          instructor.SOD_SiteId ||
          instructor.siteId ||
          instructor.SiteID ||
          "",
      );
      // Initialize Partner/Pathway from instructor doc if present
      setPartner(instructor.Partner || instructor.partner || "");
      setPathway(instructor.Pathway || instructor.pathway || "");

      // Extended instructor fields
      setDateOfBirth(instructor.dateOfBirth || "");
      setExt(instructor.ext || "");
      setNotes(instructor.notes || "");
      setSccId(instructor.SccID || "");
      setSinclairEmail(instructor.sinclair_email || "");
      setELearnInstructor(instructor.eLearn_instructor || false);
      setDentalInstructor(instructor.dental_instructor || false);
      setPltwInstructor(instructor.pltw_instructor || false);
      setAcademicInstructor(instructor.academic_instructor || false);

      // User types
      setUserTypes({
        staff: instructor.userTypes?.staff || false,
        coordinator: instructor.userTypes?.coordinator || false,
        instructor: instructor.userTypes?.instructor || false,
        admin: instructor.userTypes?.admin || false,
        pathwayManager: instructor.userTypes?.pathwayManager || false,
      });
    }
  }, [instructor]);

  // Load Sites for dropdown
  useEffect(() => {
    const loadSites = async () => {
      try {
        setSitesLoading(true);
        const snap = await getDocs(collection(db, "Sites"));
        const data = snap.docs.map((d) => ({
          id: d.id,
          SiteID: d.data().SiteID,
          SiteName: d.data().SiteName,
          ...d.data(),
        }));
        data.sort((a, b) => (a.SiteName || "").localeCompare(b.SiteName || ""));
        setSites(data);
      } catch (e) {
        console.error("Error loading Sites in EditTpInstructor:", e);
        setSites([]);
      } finally {
        setSitesLoading(false);
      }
    };

    loadSites();
  }, []);

  // Load TpPartners for the Partner dropdown. Each partner doc carries a
  // Sites array ({SiteID, SiteName}) set on the Partners maintenance screen,
  // which is what narrows the Site dropdown once a Partner is selected.
  useEffect(() => {
    const loadPartners = async () => {
      try {
        setPartnersLoading(true);
        const snap = await getDocs(collection(db, "TpPartners"));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        data.sort((a, b) =>
          (a.Tech_Prep_Partner || "").localeCompare(b.Tech_Prep_Partner || ""),
        );
        setPartners(data);
      } catch (e) {
        console.error("Error loading Partners in EditTpInstructor:", e);
        setPartners([]);
      } finally {
        setPartnersLoading(false);
      }
    };

    loadPartners();
  }, []);

  // Pathways (from PathwaysPrograms).
  useEffect(() => {
    const loadPathways = async () => {
      try {
        setPathwaysLoading(true);
        const pwSnap = await getDocs(collection(db, "PathwaysPrograms"));
        const pwList = pwSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        pwList.sort((a, b) =>
          (a.PathwayName || "").localeCompare(b.PathwayName || ""),
        );
        setPathways(pwList);
      } catch (e) {
        console.error("Error loading pathways in EditTpInstructor:", e);
        setPathways([]);
      } finally {
        setPathwaysLoading(false);
      }
    };

    loadPathways();
  }, []);

  const handleUserTypeChange = (type) => (e) => {
    setUserTypes((prev) => ({ ...prev, [type]: e.target.checked }));
  };

  const handleSiteChange = (e) => {
    setSiteId(e.target.value);
  };

  // Sites tied to the selected Partner (from TpPartners.Sites), so the Site
  // dropdown only offers sites that belong to that partner. Falls back to
  // the full Sites list when no partner is selected or the partner has no
  // sites configured yet.
  const selectedPartnerDoc = partners.find(
    (p) => p.Tech_Prep_Partner === partner,
  );
  const partnerSites =
    selectedPartnerDoc?.Sites && selectedPartnerDoc.Sites.length > 0
      ? selectedPartnerDoc.Sites
      : null;
  const siteOptions = partnerSites || sites;

  const handlePartnerChange = (e) => {
    const newPartner = e.target.value;
    setPartner(newPartner);

    const newPartnerDoc = partners.find(
      (p) => p.Tech_Prep_Partner === newPartner,
    );
    const allowedSiteIds = (newPartnerDoc?.Sites || []).map((s) => s.SiteID);
    if (allowedSiteIds.length > 0 && siteId && !allowedSiteIds.includes(siteId)) {
      setSiteId("");
    }
  };

  const handlePathwayChange = (e) => setPathway(e.target.value);

  const handleActiveToggle = (e) => {
    setActive(e.target.checked ? "-1" : "0");
  };

  const handleSubmit = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setSnackbarMessage("Email is required");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      // Email touches Firebase Auth (not just Firestore), so it's routed
      // through the dedicated updateUserEmail callable — which updates both
      // Auth and the users doc — instead of the plain Firestore updateDoc
      // below. Only called when the email actually changed.
      if (trimmedEmail !== originalEmail) {
        const emailResult = await updateUserEmail(instructor.id, trimmedEmail);
        if (!emailResult.success) {
          setSnackbarMessage(emailResult.message || "Error updating email");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
          setLoading(false);
          return;
        }
        setOriginalEmail(trimmedEmail);
      }

      // Update Firestore directly for non-sensitive instructor fields
      const userRef = doc(db, "users", instructor.id);
      const updateData = {};
      updateData.firstName = firstName;
      updateData.lastName = lastName;
      updateData.name = joinName(firstName, lastName);
      if (phone !== undefined) updateData.phone = phone;
      if (userTypes !== undefined) updateData.userTypes = userTypes;
      updateData.techPrepAdminFlag = userTypes.admin || false;

      // Instructor-specific fields
      if (dateOfBirth !== undefined) updateData.dateOfBirth = dateOfBirth;
      if (ext !== undefined) updateData.ext = ext;
      if (notes !== undefined) updateData.notes = notes;
      if (sccId !== undefined) updateData.SccID = sccId;
      if (sinclairEmail !== undefined) updateData.sinclair_email = sinclairEmail;
      updateData.eLearn_instructor = eLearnInstructor;
      updateData.dental_instructor = dentalInstructor;
      updateData.pltw_instructor = pltwInstructor;
      updateData.academic_instructor = academicInstructor;

      // Site assignment - write SOD_Site_ID (consistent with other parts of app)
      if (siteId !== undefined) updateData.SOD_Site_ID = siteId;
      // Partner and Pathway fields
      if (partner !== undefined) updateData.Partner = partner;
      if (pathway !== undefined) updateData.Pathway = pathway;
      // Active flag: -1 active, 0 inactive
      if (active !== undefined) updateData.Active = active;

      if (Object.keys(updateData).length > 0) {
        updateData.updated_at = serverTimestamp();
        await updateDoc(userRef, updateData);
      }

      // show success snackbar at top-center
      setSnackbarMessage("Instructor updated");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating instructor (Firestore):", error);
      setSnackbarMessage(error.message || "Error updating instructor");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this instructor? This action cannot be undone.",
      )
    ) {
      setDeleteLoading(true);
      try {
        // deleteUser removes both the Firebase Auth account and the
        // Firestore users doc (a plain deleteDoc here only removed the
        // Firestore doc, leaving the Auth account behind and blocking
        // re-creating an instructor with that same email).
        const result = await deleteUser(instructor.id);
        if (!result.success) {
          throw new Error(result.message || "Error deleting instructor");
        }
        setSnackbarMessage("Instructor deleted");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        if (onUserDeleted) onUserDeleted();
        onClose();
      } catch (error) {
        console.error("Error deleting instructor:", error);
        setSnackbarMessage(
          error.message || "Error deleting instructor. Please try again.",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  // DeliveryDetails records carry created_at/updatedAt Firestore Timestamps
  // (either a real Timestamp instance, or the plain {seconds, nanoseconds}
  // shape) — rendering one of those directly as a child crashes React, so
  // format it to a date string instead of showing it raw like every other
  // field below.
  const formatDeliveryDetailValue = (value) => {
    if (
      value &&
      typeof value === "object" &&
      (typeof value.toDate === "function" || "seconds" in value)
    ) {
      return formatDateTime(value) || "N/A";
    }
    return value || "N/A";
  };

  const renderDeliveryDetails = () => {
    if (
      !instructor.deliveryDetails ||
      instructor.deliveryDetails.length === 0
    ) {
      return (
        <Box p={2}>
          <Typography variant="body2" color="text.secondary">
            No delivery details found for this instructor.
          </Typography>
        </Box>
      );
    }

    return instructor.deliveryDetails.map((detail, index) => (
      <Paper key={index} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Delivery Detail {index + 1}
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(detail).map(([key, value]) => {
            if (key === "id") return null; // Skip the ID field
            return (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                >
                  {key.charAt(0).toUpperCase() +
                    key.slice(1).replace(/([A-Z])/g, " $1")}
                </Typography>
                <Typography variant="body2">
                  {formatDeliveryDetailValue(value)}
                </Typography>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    ));
  };

  return (
    <Dialog open={open} onClose={handleCloseAttempt} maxWidth="lg" fullWidth>
      <ModalDialogTitle onClose={handleCloseAttempt}>
        Edit Instructor: {instructor?.name}
      </ModalDialogTitle>
      <DialogContent>
        <Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          {/* Basic Information Section */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Basic Information</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="First Name"
                    type="text"
                    fullWidth
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Last Name"
                    type="text"
                    fullWidth
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Email"
                    type="email"
                    fullWidth
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    helperText="Updates both the sign-in account and this record"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Phone"
                    type="text"
                    fullWidth
                    value={phone}
                    onChange={handlePhoneChange(setPhone)}
                    placeholder="(xxx) xxx-xxxx"
                    inputProps={{ maxLength: 14 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Ext"
                    type="text"
                    fullWidth
                    value={ext}
                    onChange={(e) => setExt(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Date of Birth"
                    type="date"
                    fullWidth
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {/* Partner select */}
                  {partnersLoading ? (
                    <TextField
                      margin="dense"
                      label="Partner"
                      fullWidth
                      disabled
                      value="Loading partners..."
                    />
                  ) : (
                    <TextField
                      select
                      margin="dense"
                      label="Partner"
                      fullWidth
                      value={partner}
                      onChange={handlePartnerChange}
                    >
                      <MenuItem value="">No Partner</MenuItem>
                      {partners
                        .filter((p) => p.Tech_Prep_Partner)
                        .map((p) => (
                          <MenuItem key={p.id} value={p.Tech_Prep_Partner}>
                            {p.Tech_Prep_Partner}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {/* Site select — narrowed to the selected Partner's Sites */}
                  {sitesLoading ? (
                    <TextField
                      margin="dense"
                      label="Site"
                      fullWidth
                      disabled
                      value="Loading sites..."
                    />
                  ) : (
                    <TextField
                      select
                      margin="dense"
                      label="Site"
                      fullWidth
                      value={siteId}
                      onChange={handleSiteChange}
                      helperText={
                        partnerSites
                          ? "Showing only sites for the selected partner"
                          : undefined
                      }
                    >
                      <MenuItem value="">No Site</MenuItem>
                      {siteOptions.map((s) => (
                        <MenuItem key={s.SiteID || s.id} value={s.SiteID || s.id}>
                          {s.SiteName || s.SiteID}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {/* Pathway select */}
                  {pathwaysLoading ? (
                    <TextField
                      margin="dense"
                      label="Pathway"
                      fullWidth
                      disabled
                      value="Loading pathways..."
                    />
                  ) : (
                    <TextField
                      select
                      margin="dense"
                      label="Pathway"
                      fullWidth
                      value={pathway}
                      onChange={handlePathwayChange}
                    >
                      <MenuItem value="">No Pathway</MenuItem>
                      {pathways.map((pw) => (
                        <MenuItem key={pw.id} value={pw.PathwayID}>
                          {pw.PathwayName}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                {/* Active/Inactive switch moved below the dropdowns */}
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={String(active) === "-1"}
                        onChange={handleActiveToggle}
                        color="primary"
                      />
                    }
                    label={String(active) === "-1" ? "Active" : "Inactive"}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Instructor Details Section (instructor-only fields) */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Instructor Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={eLearnInstructor}
                        onChange={(e) => setELearnInstructor(e.target.checked)}
                      />
                    }
                    label="eLearn Instructor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dentalInstructor}
                        onChange={(e) => setDentalInstructor(e.target.checked)}
                      />
                    }
                    label="Dental Instructor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={pltwInstructor}
                        onChange={(e) => setPltwInstructor(e.target.checked)}
                      />
                    }
                    label="PLTW Instructor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={academicInstructor}
                        onChange={(e) => setAcademicInstructor(e.target.checked)}
                      />
                    }
                    label="Academic Instructor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="SCC ID"
                    type="text"
                    fullWidth
                    value={sccId}
                    onChange={(e) => setSccId(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Sinclair Email"
                    type="email"
                    fullWidth
                    value={sinclairEmail}
                    onChange={(e) => setSinclairEmail(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Notes"
                    type="text"
                    fullWidth
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* User Types Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">User Types</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userTypes.staff}
                        onChange={handleUserTypeChange("staff")}
                      />
                    }
                    label="Staff"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userTypes.coordinator}
                        onChange={handleUserTypeChange("coordinator")}
                      />
                    }
                    label="Coordinator"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userTypes.instructor}
                        onChange={handleUserTypeChange("instructor")}
                      />
                    }
                    label="Instructor"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userTypes.admin}
                        onChange={handleUserTypeChange("admin")}
                      />
                    }
                    label="Admin"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userTypes.pathwayManager}
                        onChange={handleUserTypeChange("pathwayManager")}
                      />
                    }
                    label="Pathway Manager"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Delivery Details Section (Read-Only) */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">
                Delivery Details (Read-Only)
                <Chip
                  label={`${instructor?.deliveryDetails?.length || 0} records`}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>{renderDeliveryDetails()}</AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <ModalActions
        onSave={handleSubmit}
        saving={loading}
        onDelete={handleDelete}
        deleting={deleteLoading}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog open={confirmCloseOpen} onClose={handleCancelClose} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EditTpInstructor;
